// The data-source seam (PRD task-031 + task-017).
//
// WHY AN INTERFACE: the same <storadar-map> component renders in two contexts:
//   1. LIVE (visitor on the customer's site) — hits the public widget API,
//      GET /widget/{token}/config and /locations. See LiveDataSource.
//   2. PREVIEW (task-017 scrape-preview funnel) — renders from an in-memory
//      JSON payload produced by the scraper, with NO network calls, so a
//      prospect sees their locations before an account exists. See
//      PreviewDataSource.
//
// The component depends only on this interface; it never constructs a fetch or
// touches a URL directly. That is the whole seam: swap the implementation and
// the component is unchanged. Injection point is StoradarMap#dataSource (a
// public property) or an inline <script type="application/json"> child — see
// StoradarMap for how one is chosen.

import type { AutocompletePayload, ConfigPayload, LocationDetail, ViewportPayload, ViewportQuery } from "./types";

/** Distinguishes an honest failure from an empty-but-successful result. */
export class DataSourceError extends Error {
  /**
   * @param retryable whether a retry affordance makes sense (network / 5xx / 429
   *   rate-limit yes; a 404 unknown-token no — retrying will not conjure the
   *   locator).
   * @param retryAfterSeconds server-advised minimum wait before retrying, parsed
   *   from a 429 `Retry-After` header. Null when not advised. The UI holds the
   *   retry affordance disabled for this long so a retry can't immediately
   *   re-trip the limiter.
   */
  constructor(
    message: string,
    readonly retryable: boolean,
    readonly retryAfterSeconds: number | null = null,
  ) {
    super(message);
    this.name = "DataSourceError";
  }
}

/**
 * The pluggable source of everything the component renders. Both methods either
 * resolve with a payload or reject with a [DataSourceError]; they never resolve
 * with a silent fallback (house rule: visible failure over silent fallback).
 */
export interface DataSource {
  /** Boot config + theme + baked entitlement. Rejects on network failure / 404. */
  loadConfig(): Promise<ConfigPayload>;

  /**
   * Locations for a viewport (clusters or pins per the payload's `mode`).
   * [signal] lets the component abort an in-flight fetch when the map moves
   * again before the previous request settled (debounced refetch).
   */
  loadViewport(query: ViewportQuery, signal?: AbortSignal): Promise<ViewportPayload>;

  /**
   * Search-box suggestions for [query] (task-032, F-032). Unlike the other two
   * methods this does NOT reject on a provider-side outage — [AutocompletePayload.available]
   * is the typed-failure signal instead, so the search box can show a quiet
   * "search unavailable" state without disturbing the map (which still works).
   * Only rejects (network failure / unknown token) the same way [loadConfig] does.
   */
  loadAutocomplete(query: string, signal?: AbortSignal): Promise<AutocompletePayload>;

  /**
   * Full detail for one location (task-033), fetched lazily when a visitor
   * selects a pin/row. Rejects with a [DataSourceError] the same way [loadConfig]
   * does — a 404 (unknown token, or a location no longer visible) is a terminal,
   * non-retryable failure; a network/5xx problem is retryable.
   */
  loadDetail(id: string, signal?: AbortSignal): Promise<LocationDetail>;
}
